import { FC, useMemo, useState } from 'react';

import { ModeEditOutline } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { InvoiceStatus } from '@prisma/client';
import { BsEye, BsX } from 'react-icons/bs';
import { PiPaperPlaneTilt } from 'react-icons/pi';

import { useParams } from '@redwoodjs/router';
import { useMutation, useQuery } from '@redwoodjs/web';

import { TotalCard } from 'src/components';
import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import ViewPDFModal from 'src/components/ViewPDFModal';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { BillingDetails } from 'src/features/finance/sepaDirectDebits/components';
import { UPDATE_SELF_BILLING_INVOICE_STATUS } from 'src/graphql/mutations/selfBillingInvoice.mutation';
import { GET_SELF_BILLING_INVOICE } from 'src/graphql/queries/getSelfBillingInvoices.query';
import { PageHeader } from 'src/layouts/MainLayout/PageHeader';
import { EnumToArray, FormatBillingDetails, enumToCamelCase } from 'src/lib/formatters';
import { onError } from 'src/utils/utils';

import { SelfBillingInvoiceDetails } from '../ViewSelfBillingInvoicePage/components';

import { CommentsSection, InvoiceItems } from './components';
import { SendSelfBillingInvoiceModal } from './components/SendSelfBillingInvoiceModal';
import TestPDF from './test.pdf';
const ViewSelfBillingInvoicePage: FC = () => {
    const { id } = useParams();
    const { usersCurrency } = useAuthContext();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
    const [isSendModalOpen, setIsSendModalOpen] = useState(false);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [selfBillingInvoiceData, setSelfBillingInvoiceData] = useState();

    const { loading, data, refetch } = useQuery(GET_SELF_BILLING_INVOICE, {
        onError,
        variables: { id: Number.parseInt(id) },
        onCompleted: (res) => {
            setSelfBillingInvoiceData(res?.selfBillingInvoice);
        },
    });

    const billingDetails = useMemo(() => {
        const billingContactInformation =
            selfBillingInvoiceData?.partner?.billingContactInformation;
        return FormatBillingDetails(billingContactInformation);
    }, [selfBillingInvoiceData]);

    const [updateSelfBillingInvoiceStatus] = useMutation(UPDATE_SELF_BILLING_INVOICE_STATUS, {
        variables: { id: Number.parseInt(id) },
        onError: (error) => {
            return showErrorToast(error.message);
        },
        onCompleted: () => {
            showSuccessToast('Self-Billing Invoice Status Updated!');
            setStatusMenuOpen(false);
            onRefreshAll();
            setAnchorEl(null);
        },
    });

    const onRefreshAll = () => {
        refetch();
    };

    const onUpdateStatus = (e: any) => {
        setStatusMenuOpen(true);
        setAnchorEl(e?.currentTarget);
    };

    const onStatusSelect = async (status: InvoiceStatus) => {
        await updateSelfBillingInvoiceStatus({
            variables: { status, invoiceId: id },
        });
    };

    const handleViewPDFModal = () => {
        setIsPDFModalOpen(true);
    };

    const handleOpenSendModal = () => {
        setIsSendModalOpen(true);
    };

    const handleCloseSendModal = () => {
        setIsSendModalOpen(false);
    };

    return (
        <Flex flexDirection="column" gap="24px">
            <PageHeader
                heading="View Self-Billing Invoice"
                callToActions={
                    <Grid
                        item
                        md={7}
                        xs={12}
                        display="flex"
                        container
                        alignItems="end"
                        justifyContent="end"
                        gap={2}
                    >
                        <Button
                            key="0-handleView"
                            variant="npi-primary"
                            startIcon={<BsEye />}
                            onClick={handleViewPDFModal}
                            sx={{
                                minWidth: '40px !important',
                                width: '40px',
                                height: '40px',
                                padding: '0px',
                                '& span': {
                                    marginRight: '0px',
                                    marginLeft: '0px',
                                },
                                boxShadow: 'none',
                            }}
                        />
                        <Button
                            key="1-handleSend"
                            variant="npi-primary"
                            startIcon={<PiPaperPlaneTilt />}
                            onClick={handleOpenSendModal}
                            sx={{
                                minWidth: '40px !important',
                                width: '40px',
                                height: '40px',
                                padding: '0px',
                                '& span': {
                                    marginRight: '0px',
                                    marginLeft: '0px',
                                },
                                boxShadow: 'none',
                            }}
                        />
                        <Button
                            key="2-handleUpdate"
                            startIcon={<ModeEditOutline />}
                            variant="npi-primary"
                            onClick={onUpdateStatus}
                        >
                            Update Status
                        </Button>
                    </Grid>
                }
            />
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={statusMenuOpen}
                onClose={() => {
                    return setStatusMenuOpen(false);
                }}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {EnumToArray(InvoiceStatus)?.map((item) => {
                    return (
                        <MenuItem
                            key={item}
                            onClick={() => {
                                return onStatusSelect(item);
                            }}
                        >
                            {enumToCamelCase(item)}
                        </MenuItem>
                    );
                })}
            </Menu>
            {loading ? (
                <Loader />
            ) : (
                <>
                    <Flex gap="inherit">
                        <Flex flexDirection="column" flex={4}>
                            <SelfBillingInvoiceDetails
                                invoiceDate={data?.selfBillingInvoice?.invoiceDate ?? ''}
                                partnerName={
                                    data?.selfBillingInvoice?.partner?.business?.legalName ?? ''
                                }
                                invoiceNumber={222}
                                invoiceStatus={InvoiceStatus.PAID}
                            />
                        </Flex>

                        <Flex flexDirection="column" flex={8} gap="24px">
                            <BillingDetails
                                billingAddress={billingDetails.billingAddress}
                                contactNumber={billingDetails.contactNumber}
                                name={billingDetails?.billingContact}
                                email={billingDetails.email}
                            />
                        </Flex>
                    </Flex>
                    <Box display={'flex'} gap="19px" justifyContent={'space-between'}>
                        <TotalCard
                            title="Total Net Amount"
                            value={data?.selfBillingInvoice?.totals?.netAmount}
                            currency={usersCurrency}
                            labelTypographyStyles={{
                                fontSize: '18px',
                                fontWeight: '600',
                            }}
                            valueTypographyStyles={{
                                fontSize: '24px',
                            }}
                            containerStyles={{
                                borderRadius: '16px',
                                width: '100%',
                            }}
                            titleContainerStyles={{
                                borderTopLeftRadius: '15px',
                                borderTopRightRadius: '15px',
                                height: '100%',
                            }}
                        />
                        <TotalCard
                            title={`Total Vat Amount`}
                            value={data?.selfBillingInvoice?.totals?.vatAmount}
                            currency={usersCurrency}
                            labelTypographyStyles={{
                                fontSize: '18px',
                                fontWeight: '600',
                            }}
                            valueTypographyStyles={{
                                fontSize: '24px',
                            }}
                            containerStyles={{
                                borderRadius: '16px',
                                width: '100%',
                            }}
                            titleContainerStyles={{
                                borderTopLeftRadius: '15px',
                                borderTopRightRadius: '15px',
                                height: '100%',
                            }}
                        />
                        <TotalCard
                            title="Total Invoice Amount"
                            value={data?.selfBillingInvoice?.totals?.invoiceAmount}
                            currency={usersCurrency}
                            labelTypographyStyles={{
                                fontSize: '18px',
                                fontWeight: '600',
                            }}
                            valueTypographyStyles={{
                                fontSize: '24px',
                            }}
                            containerStyles={{
                                borderRadius: '16px',
                                width: '100%',
                            }}
                            titleContainerStyles={{
                                borderTopLeftRadius: '15px',
                                borderTopRightRadius: '15px',
                                height: '100%',
                            }}
                        />
                    </Box>
                    <InvoiceItems lineItems={data?.selfBillingInvoice?.invoiceItems} />
                    <CommentsSection comments={data?.selfBillingInvoice?.comments} />

                    <ViewPDFModal
                        title="View Self-Billing Invoice PDF"
                        file={TestPDF}
                        isModalOpen={isPDFModalOpen}
                        setIsModalOpen={setIsPDFModalOpen}
                    />
                    <Dialog
                        open={isSendModalOpen}
                        onClose={handleCloseSendModal}
                        aria-labelledby="send-pdf-modal"
                        sx={{
                            '& .MuiPaper-root': {
                                margin: '0px',
                                padding: '0px',
                                borderRadius: '16px',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                background: 'linear-gradient(90deg, #FF8008, #FFC837)',
                                height: '82px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '24px',
                            }}
                        >
                            <Typography color="white" variant="h2">
                                Resend Self-Billing Invoice
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<BsX width="24px" height="24px" />}
                                onClick={handleCloseSendModal}
                                sx={{
                                    color: '#F39E4E',
                                    minWidth: '32px !important',
                                    width: '32px',
                                    height: '32px',
                                    padding: '0px',
                                    borderRadius: '50%',
                                    backgroundColor: 'white',
                                    border: '1px solid #F39E4E',
                                    '& span': {
                                        marginRight: '0px',
                                        marginLeft: '0px',
                                    },
                                    '&:hover': {
                                        backgroundColor: '#eee !important',
                                    },
                                    boxShadow: 'none',
                                }}
                            />
                        </Box>
                        <DialogContent>
                            <SendSelfBillingInvoiceModal
                                handleCloseSendModal={handleCloseSendModal}
                                invoiceId={Number.parseInt(id)}
                                partnerBillingContactEmail={billingDetails.email}
                            />
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </Flex>
    );
};

export default ViewSelfBillingInvoicePage;
